// pages/Dashboard/Finances/SettlementHistory.tsx
import React from 'react';
import { CheckCircle, Clock, XCircle, ArrowRight, ArrowLeft, History } from 'lucide-react';
import { useLanguage } from '../../../hooks/useLanguage';

interface Settlement {
    _id: string;
    type: 'platform_to_garage' | 'garage_to_platform';
    amount: number;
    status: 'completed' | 'pending' | 'cancelled';
    paymentMethod?: string;
    transactionRef?: string;
    notes?: string;
    createdAt: string;
    processedAt?: string;
}

interface SettlementHistoryProps {
    settlements: Settlement[];
}

export const SettlementHistory: React.FC<SettlementHistoryProps> = ({ settlements }) => {
    const { t, isRTL, currentLanguage } = useLanguage();

    if (!settlements || settlements.length === 0) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" dir={isRTL ? 'rtl' : 'ltr'}>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <History size={20} className="text-indigo-600" />
                    {t('finances.settlements.title')}
                </h3>
                <div className="text-center py-8 text-gray-500">
                    {t('finances.settlements.noData')}
                </div>
            </div>
        );
    }

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'completed':
                return (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        <CheckCircle size={14} />
                        {t('finances.settlements.status.completed')}
                    </span>
                );
            case 'pending':
                return (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                        <Clock size={14} />
                        {t('finances.settlements.status.pending')}
                    </span>
                );
            case 'cancelled':
                return (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                        <XCircle size={14} />
                        {t('finances.settlements.status.cancelled')}
                    </span>
                );
            default:
                return null;
        }
    };

    const getTypeLabel = (type: string) => {
        if (type === 'platform_to_garage') {
            return (
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-600">
                    <ArrowLeft size={16} />
                    {t('finances.settlements.type.received')}
                </span>
            );
        } else {
            return (
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-600">
                    <ArrowRight size={16} />
                    {t('finances.settlements.type.paid')}
                </span>
            );
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" dir={isRTL ? 'rtl' : 'ltr'}>
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <History size={20} className="text-indigo-600" />
                {t('finances.settlements.title')}
            </h3>

            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                        <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                            {t('finances.settlements.table.date')}
                        </th>
                        <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                            {t('finances.settlements.table.type')}
                        </th>
                        <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                            {t('finances.settlements.table.amount')}
                        </th>
                        <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                            {t('finances.settlements.table.method')}
                        </th>
                        <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                            {t('finances.settlements.table.reference')}
                        </th>
                        <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
                            {t('finances.settlements.table.status')}
                        </th>
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {settlements.map((settlement) => (
                        <tr key={settlement._id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                {new Date(settlement.createdAt).toLocaleDateString(
                                    currentLanguage === 'ar' ? 'ar-SA' :
                                        currentLanguage === 'en' ? 'en-GB' : 'fr-FR',
                                    { day: '2-digit', month: '2-digit', year: 'numeric' }
                                )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                {getTypeLabel(settlement.type)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                                {settlement.amount.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} SAR
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                {settlement.paymentMethod || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">
                                {settlement.transactionRef || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                {getStatusBadge(settlement.status)}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            {/* Summary */}
            <div className="mt-6 pt-6 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-green-800 font-medium mb-1">
                        {t('finances.settlements.summary.totalReceived')}
                    </p>
                    <p className="text-2xl font-bold text-green-600">
                        {settlements
                            .filter(s => s.type === 'platform_to_garage' && s.status === 'completed')
                            .reduce((sum, s) => sum + s.amount, 0)
                            .toLocaleString('fr-FR', { minimumFractionDigits: 2 })} SAR
                    </p>
                </div>

                <div className="bg-orange-50 p-4 rounded-lg">
                    <p className="text-sm text-orange-800 font-medium mb-1">
                        {t('finances.settlements.summary.totalPaid')}
                    </p>
                    <p className="text-2xl font-bold text-orange-600">
                        {settlements
                            .filter(s => s.type === 'garage_to_platform' && s.status === 'completed')
                            .reduce((sum, s) => sum + s.amount, 0)
                            .toLocaleString('fr-FR', { minimumFractionDigits: 2 })} SAR
                    </p>
                </div>

                <div className="bg-yellow-50 p-4 rounded-lg">
                    <p className="text-sm text-yellow-800 font-medium mb-1">
                        {t('finances.settlements.summary.pending')}
                    </p>
                    <p className="text-2xl font-bold text-yellow-600">
                        {settlements
                            .filter(s => s.status === 'pending')
                            .reduce((sum, s) => sum + s.amount, 0)
                            .toLocaleString('fr-FR', { minimumFractionDigits: 2 })} SAR
                    </p>
                </div>
            </div>
        </div>
    );
};